import {
  APIParentChildReferenceAttributes,
  APIReferenceAttributes,
  ComponentPath,
} from './apiTypes';
import { ArdoqId, WithTransactionId } from './coreTypes';
import { APIScopeData } from './scopeDataAPITypes';
import { ComponentSelection, TraversalParams, Traversal } from './traversal';

export enum LoadedStateType {
  /**
   * Persisted only. A search is a traversal with no hops, so it appears in
   * stored slides, bookmarks and shared URLs but is never held in memory: it is
   * part of {@link PersistedLoadedStateParams}, not of
   * {@link LoadedStateParams}, and normalizing persisted data maps it to
   * {@link LoadedStateType.TRAVERSAL}. It occupies index 0 of every codec's
   * `loadedTypesArray`, which is the wire format, so it cannot be removed.
   * Nothing writes it any more: a traversal that is nothing but a component set
   * persists as {@link LoadedStateType.TRAVERSAL} like any other.
   */
  SEARCH = 'search',
  TRAVERSAL = 'traversal',
  CREATED_ITEMS = 'createdItems',
  /**
   * Persisted only. Produced by the removed details-drawer triple toggler, so it
   * appears in stored slides, bookmarks and shared URLs but is never created
   * again. It is part of {@link PersistedLoadedStateParams}, not of
   * {@link LoadedStateParams} — normalizing persisted data maps it to
   * {@link LoadedStateType.TRAVERSAL}. It also occupies index 3 of every codec's
   * `loadedTypesArray`, which is the wire format, so it cannot be removed.
   */
  TRAVERSAL_CREATED_IN_VIEW = 'traversalCreatedInView',
}

export type LoadedStateHash = string;

type Hidable = {
  isHidden: boolean;
};

/**
 * A stable id assigned to a runtime `LoadedState` (never persisted) so it can
 * be used as a namespace, e.g. for RxBeach actions scoped to that entry.
 * Generated once, where a `LoadedState` is constructed (`attachLoadedGraph`),
 * and carried through unchanged from then on.
 */
export type WithUuid = {
  uuid: string;
};

export type LoadedGraphChunk = {
  /**
   * The scope data for this loaded state. Each loaded state carries its own
   * scope data; websocket routines keep it in sync (create / update / delete of
   * components, references, fields, models, workspaces and tags). In viewpoint
   * mode it is the source of truth; the model interfaces read from it for as long
   * as they're in use (a transitional construct, meant to disappear).
   */
  scopeData: APIScopeData;
  /**
   * Used for LoadedStateEntryTraversal or when a LoadedStateEntrySearch is
   * created from a query. Basically the result of an executed search or traversal.
   * 'data` attributes on various LoadedStates are the related request arguments.
   */
  componentIdsAndReferences: LoadedComponentIdsAndReferences;
};

/**
 * Changing this type requires a change in the backend and in the url encoding/decoding.
 * Loaded states are encoded and decoded in the URL on the frontend.
 * On the backend they stored in the database as part of slides and backend
 * iterates over them to create aggregated scope data for presentation in presentation mode.
 */
export type SearchLoadedStateParams = Hidable &
  WithTransactionId & {
    type: LoadedStateType.SEARCH;
    data: {
      componentSelection: ComponentSelection;
    };
  };

/**
 * This loaded state entry contains created components and references.
 * Changing this type requires a change in the backend and in the url encoding/decoding.
 * Loaded states are encoded and decoded in the URL on the frontend.
 * On the backend they stored in the database as part of slides and backend
 * iterates over them to create aggregated scope data for presentation in presentation mode.
 */
export type CreatedItemsLoadedStateParams = Hidable &
  WithTransactionId & {
    type: LoadedStateType.CREATED_ITEMS;
    data: {
      componentIds: ArdoqId[];
      referenceIds: ArdoqId[];
    };
  };
export type CreatedItemsLoadedState = CreatedItemsLoadedStateParams &
  LoadedGraphChunk &
  WithUuid;

export type LoadedComponentIdsAndReferences = {
  componentIds: ArdoqId[];
  references: APIReferenceAttributes[];
  /**
   * Parent/child relations represented as references.
   * In this structure this is the odd one out as it is some kind of
   * extension/addition to scopeData.
   */
  parentChildReferences: APIParentChildReferenceAttributes[];
  startSetResult: string[];
  pathResults: ComponentPath[][];
};

/**
 * This loaded state represents a traversal that has been created in viewpoint builder or executed saved viewpoint.
 *
 * Changing this type requires a change in the backend and in the url encoding/decoding.
 * Loaded states are encoded and decoded in the URL on the frontend.
 * On the backend they stored in the database as part of slides and backend
 * iterates over them to create aggregated scope data for presentation in presentation mode.
 */
export type TraversalLoadedStateParams = Hidable &
  WithTransactionId & {
    type: LoadedStateType.TRAVERSAL;
    traversalId?: string;
    data: Traversal;
  };
export type TraversalLoadedState = TraversalLoadedStateParams &
  LoadedGraphChunk &
  WithUuid;

/**
 * Persisted only — see {@link LoadedStateType.TRAVERSAL_CREATED_IN_VIEW}. Shaped
 * exactly like a traversal without a `traversalId`, since `Traversal` is an
 * alias of `TraversalParams`, which is why normalizing it to a traversal loses
 * nothing.
 */
export type TraversalCreatedInViewLoadedStateParams = Hidable &
  WithTransactionId & {
    type: LoadedStateType.TRAVERSAL_CREATED_IN_VIEW;
    data: TraversalParams;
  };

export type LoadedState = TraversalLoadedState | CreatedItemsLoadedState;

/**
 * What the app holds in memory. Everything reaching this type has been through
 * `normalizeLoadedState`, so the persisted-only variants are already mapped away.
 */
export type LoadedStateParams =
  | TraversalLoadedStateParams
  | CreatedItemsLoadedStateParams;

/**
 * What is stored in slides and bookmarks and encoded in URLs. A superset of
 * {@link LoadedStateParams}: it also carries variants that were retired from the
 * runtime model but survive in data we have already written.
 *
 * Read sites must normalize before treating persisted data as
 * {@link LoadedStateParams} — the two are deliberately distinct so the compiler
 * catches a missing normalization rather than leaving it to review.
 */
export type PersistedLoadedStateParams =
  | LoadedStateParams
  | SearchLoadedStateParams
  | TraversalCreatedInViewLoadedStateParams;

/**
 * Narrows a persisted state to the retired search variant. Persisted data only —
 * the runtime model has no such variant.
 */
export const isSearchLoadedState = (
  state: PersistedLoadedStateParams
): state is SearchLoadedStateParams => state.type === LoadedStateType.SEARCH;

type IsCreatedItemsLoadedState = {
  (state: LoadedState): state is CreatedItemsLoadedState;
  (state: LoadedStateParams): state is CreatedItemsLoadedStateParams;
  (state: PersistedLoadedStateParams): state is CreatedItemsLoadedStateParams;
};
export const isCreatedItemsLoadedState: IsCreatedItemsLoadedState = (
  state: LoadedState | PersistedLoadedStateParams
): state is CreatedItemsLoadedState =>
  state.type === LoadedStateType.CREATED_ITEMS;

type IsTraversalLoadedState = {
  (state: LoadedState): state is TraversalLoadedState;
  (state: LoadedStateParams): state is TraversalLoadedStateParams;
};
export const isTraversalLoadedState: IsTraversalLoadedState = (
  state: LoadedState | LoadedStateParams
): state is TraversalLoadedState => state.type === LoadedStateType.TRAVERSAL;

/**
 * Narrows a persisted state to the retired variant. Persisted data only — the
 * runtime model has no such variant.
 */
export const isTraversalCreatedInViewLoadedState = (
  state: PersistedLoadedStateParams
): state is TraversalCreatedInViewLoadedStateParams =>
  state.type === LoadedStateType.TRAVERSAL_CREATED_IN_VIEW;

/**
 * Both persisted traversal variants share a `data` shape and one encoder, so the
 * codecs branch on this rather than on the individual types.
 */
export const isAnyPersistedTraversalLoadedState = (
  state: PersistedLoadedStateParams
): state is
  | TraversalLoadedStateParams
  | TraversalCreatedInViewLoadedStateParams =>
  state.type === LoadedStateType.TRAVERSAL ||
  state.type === LoadedStateType.TRAVERSAL_CREATED_IN_VIEW;

/**
 * Runs on raw, un-normalized persisted slide data, which may still carry the
 * legacy `traversalCreatedInView` type, so it must not enumerate known types.
 */
export const hasLoadedState = <
  T extends PersistedLoadedStateParams | LoadedState,
>(
  loadedStates: T[]
) => loadedStates.length > 0;

export type HexIndex = string;

/**
 * The loaded state's original, concrete start set.
 *
 * Mirrors the backend `loaded-state-concrete-start-set` (presentation_service.clj):
 * a state contributes to the context switch only when it has a non-empty
 * `startSet` and no `startQuery` — a `startQuery` means the start context is
 * resolved by the query, not by this explicit set, so it is left untouched.
 *
 * Reads `data.componentSelection` by structural access; `createdItems` has no
 * `componentSelection`, and legacy data may omit `startSet`, so both default to `[]`.
 */
const readComponentSelection = (
  state: PersistedLoadedStateParams
): ComponentSelection | undefined => {
  if (isSearchLoadedState(state)) return state.data?.componentSelection;
  if (isAnyPersistedTraversalLoadedState(state))
    return state.data?.componentSelection;
  return undefined;
};

export const getConcreteStartSet = (
  state: PersistedLoadedStateParams
): ArdoqId[] => {
  const { startSet, startQuery } = readComponentSelection(state) ?? {};
  return startSet && startSet.length > 0 && !startQuery ? startSet : [];
};
